import { inject, Injectable } from "@angular/core";
import { TemplatesStateModel, Template, NameMap, StakeholderDropdown } from "./templates.model";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { TemplatesService } from "../templates.service";
import { DropdownOptionsService } from "app/modules/system-setup/dropdown-options/dropdown-options.service";
import { GetFormActionDropdowns, GetDistinctDocuments, GetTemplates, IsSelectedGridDataEditable, OnRemoveGridEntry, OnRemoveTemplate, OnSetNewGridEntry, OnSubmitValues, SetSelectedTemplate, SetTemplateFormGridConfig, SetTemplateType, SetSelectedTemplateRowData, SetIsNew, SetNameMap, SetIsGeneric, SavePurchaseShippingTerms, GetFormData, SaveSalesShippingInstructions, SaveSingleForm, SetStakeholderDropdowns, SetIsClone, SetSelectedStakeholders, SetSelectedTemplateComponent } from "./templates.actions";
import { catchError, tap, firstValueFrom } from "rxjs";
import { OnRemoveSharedGridRowData, OnSetSharedGridConfig, OnSetSharedGridRowData } from "app/modules/shared/ag-grid/shared-grid-state/shared-grid.actions";
import { SharedGridState } from "app/modules/shared/ag-grid/shared-grid-state/shared-grid.state";
import { produce } from "immer";
import { SetShowLoader, ShowToastrAlert } from "app/modules/shared-state/shared.actions";

@State<TemplatesStateModel>({
  name: 'templates',
  defaults: {
    templates: [
      {
        value: '',
        action: '',
        type: '',
        key: '',
        stakeholders: [],
        valueFooter: '',
        edited: false,
      }
    ],
    isNew: false,
    isClone: false,
    selectedTemplate: null,
    type: '',
    canOpenDialog: false,
    key: '',
    stakeholders: [],
    valueFooter: '',
    templateRowData: [],
    isSelectedRowEditable: false,
    selectedTemplateRowData: null,
    nameMap: [
      {
        _id: '',
        name: '',
        stakeholderType: '',
      },
    ],
    templateForm: {
      model: {
        value: '',
        action: '',
      },
      dirty: false,
      status: '',
      errors: {},
    },
    templateFooterForm: {
      model: {
        valueFooter: '',
        stakeholders: '',
      },
      dirty: false,
      status: '',
      errors: {},
    },
    templatePurchaseTermsForm: {
      model: {
        methodOfShipment: '',
        bookingArrangedBy: '',
        descriptionOfGoods: '',
        markingInstructions: '',
        documentsRequired: '',
        fumigation: '',
        consignee: '',
        sampleInstructions: '',
        notes: ''
      },
      dirty: false,
      status: '',
      errors: {},
    },
    templateSalesShippingInstructionsForm: {
      model: null,
      dirty: false,
      status: '',
      errors: {}
    },
    templateSingleForm: {
      model: null,
      dirty: false,
      status: '',
      errors: {},
    },
    stakeholderForm: {
      model: null,
      dirty: false,
      status: '',
      errors: {},
    },
    stakeholderDropdowns: null,
    actionFormDropdowns: {},
    isGeneric: false,
    templatePurchaseTermsFormData: null,
    templateSalesShippingInstructionsFormData: null,
    templateSingleFormFieldData: null,
    stakeholderFormData: null,
    selectedTemplateComponent: null,
    editedRowIndex: null,

  }
})

@Injectable()
export class TemplatesState {
  private _store = inject(Store);
  private _templatesService = inject(TemplatesService);
  private _dropdownService = inject(DropdownOptionsService);

  @Action(SetTemplateType)
  setTemplateType(ctx: StateContext<TemplatesStateModel>, { payload }: SetTemplateType) {
    ctx.patchState({ type: payload.templateType });
  }

  @Action(SetSelectedTemplate)
  setSelectedTemplate(ctx: StateContext<TemplatesStateModel>, { payload }: SetSelectedTemplate) {
    return ctx.patchState({
      key: payload.key,
      stakeholders: payload.stakeholders,
      type: payload.type
    });
  }

  @Action(GetTemplates)
  getTemplates(ctx: StateContext<TemplatesStateModel>, { payload }: GetTemplates) {
    return this._templatesService.getFilteredTemplates(
      {
        type: ctx.getState().type,
        key: ctx.getState().key,
        stakeholders: ctx.getState().stakeholders
      }).pipe(
        tap((result: Template[]) => {
          if (!ctx.getState().isClone) {
            ctx.dispatch(new OnSetSharedGridRowData({ payload: result, gridUUID: payload.gridUUID }));
            ctx.patchState({ templates: result });
          } else {
            const strippedResult = result.map(template => ({
              ...template,
              _id: null,
              stakeholders: [],
            }));
            ctx.dispatch(new OnSetSharedGridRowData({ payload: strippedResult, gridUUID: payload.gridUUID }));
            ctx.patchState({ stakeholders: [] });
            ctx.patchState({ templates: strippedResult });
          }
        })
      );
  }

  @Action(GetDistinctDocuments)
  async getDistinctDocuments(ctx: StateContext<TemplatesStateModel>, { payload }: GetDistinctDocuments) {
    await firstValueFrom(this._templatesService.getStakeholdersMap().pipe(
      tap((result: NameMap[]) => {
        ctx.patchState({ nameMap: result });
      })
    ));
    return this._templatesService.getDistinctDocuments().pipe(
      tap((result: Template[]) => {
        ctx.dispatch(new OnSetSharedGridRowData({
          payload: result,
          gridUUID: payload.gridUUID
        }));
        ctx.patchState({ templates: result });
      })
    );
  }

  @Action(SetTemplateFormGridConfig)
  setTemplateFormGridConfig(ctx: StateContext<TemplatesStateModel>, { payload }: SetTemplateFormGridConfig) {
    const store = inject(Store);
    const parentGridConfig = store.selectSnapshot(SharedGridState.getGridConfig);
    const updatedGridConfig = produce(parentGridConfig, (draft) => {
      draft[store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID] = payload.secondGridConfig[store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID];
    });
    ctx.dispatch(new OnSetSharedGridConfig(updatedGridConfig));
  }

  @Action(GetFormActionDropdowns)
  getFormDropdowns(
    ctx: StateContext<TemplatesStateModel>,
    { payload }: GetFormActionDropdowns
  ) {
    return this._dropdownService.getDropdownsFilter(payload).pipe(
      tap((result: any) => {
        ctx.patchState({ actionFormDropdowns: result });
      })
    );
  }

  @Action(SetStakeholderDropdowns)
  setStakeholderDropdowns(ctx: StateContext<TemplatesStateModel>) {
    const type = ctx.getState().type;
    const selectedStakeholders = ctx.getState().stakeholders ?? [];
    const nameMap = ctx.getState().nameMap;
    let stakeholdersDropdownOptions;
    this._templatesService.getAvailableStakeholders(type).pipe(
      tap((stakeholders: StakeholderDropdown[]) => {
        stakeholdersDropdownOptions = stakeholders ? [...stakeholders] : [];
        stakeholdersDropdownOptions.push(...selectedStakeholders.map(stakeholderId => {
          const matchingName = nameMap.find(mapping => mapping._id === stakeholderId);
          return {
            _id: stakeholderId,
            name: matchingName ? matchingName.name : stakeholderId,
          };
        }));
        ctx.patchState({ stakeholderDropdowns: stakeholdersDropdownOptions });
      })
    ).subscribe();
  }

  @Action(OnSetNewGridEntry)
  onSetNewGridEntry(ctx: StateContext<TemplatesStateModel>) {
    const state = ctx.getState();
    const payload = state.templateForm.model;
    const isEditable = state.isSelectedRowEditable;
    const selectedTemplateRowData = state.selectedTemplateRowData;
    const selectedTemplateComponent = state.selectedTemplateComponent;

    const gridConfig = this._store.selectSnapshot(SharedGridState.getGridConfig);
    const gridUUIDs = this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs);
    const rowData = gridConfig[gridUUIDs.secondGridUUID].rowData;

    if (isEditable) {
      if (selectedTemplateComponent._id) {
        const updatedRowData = produce(rowData, draft => {
          const index = draft.findIndex(value => value._id === selectedTemplateRowData._id);
          if (index !== -1) {
            draft[index] = { ...draft[index], ...payload, edited: true };
          }
        });
        ctx.patchState({ templates: updatedRowData });
        ctx.dispatch(new OnSetSharedGridRowData({
          update: true,
          payload: [updatedRowData.find(value => value._id === selectedTemplateRowData._id)],
          gridUUID: gridUUIDs.secondGridUUID
        }));
        ctx.dispatch(new IsSelectedGridDataEditable(false));
        return;
      } else {
        const updatedRowData = produce(rowData, draft => {
          draft[state.editedRowIndex] = { ...draft[state.editedRowIndex], ...payload, edited: true };
        });
        ctx.patchState({ templates: updatedRowData });
        ctx.dispatch(new OnSetSharedGridRowData({
          reload: true,
          payload: updatedRowData,
          gridUUID: gridUUIDs.secondGridUUID
        }));
        ctx.patchState({ templates: updatedRowData });
        ctx.dispatch(new IsSelectedGridDataEditable(false));
        return;
      }
    }

    const updatedRowData = produce(rowData, draft => {
      payload['new'] = true;
      draft.push(payload);
    });
    ctx.patchState({ templates: updatedRowData });
    ctx.dispatch(new OnSetSharedGridRowData({
      payload: [payload],
      gridUUID: gridUUIDs.secondGridUUID
    }));
  }

  @Action(OnRemoveGridEntry)
  onRemoveGridEntry(ctx: StateContext<TemplatesStateModel>, { payload }: OnRemoveGridEntry) {
    if (ctx.getState().templates.length == 1) {
      return ctx.dispatch(
        new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: `Documentation Cannot Be Empty`,
          position: 'toast-top-center',
        })
      );
    }
    if (ctx.getState().isSelectedRowEditable) {
      ctx.dispatch(new ShowToastrAlert({
        showToast: true,
        toastrType: 'error',
        messageTitle: 'Error',
        messageBody: 'You cannot delete a component while editing a documentation component.',
        position: 'toast-top-center'
      }));
      return;
    }
    if (payload._id) {
      return this._templatesService.deleteValueEntry(payload._id).pipe(
        tap(() => {
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'success',
            messageTitle: 'Success',
            messageBody: 'Template entry deleted',
            position: 'toast-top-center'
          }));
          ctx.patchState({ templates: ctx.getState().templates.filter(data => data._id !== payload._id) });
          ctx.dispatch(new OnRemoveSharedGridRowData({
            removeRow: payload,
            gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID
          }));
        }));
    } else {
      const updatedRowData = produce(ctx.getState().templates, draft => {
        return draft.filter(data => data.value !== payload.value);
      });
      this._store.dispatch(new OnSetSharedGridRowData({
        reload: true,
        payload: updatedRowData,
        gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID
      }));
      ctx.patchState({ templates: updatedRowData });
    }
  }
  @Action(SetSelectedTemplateComponent)
  setSelectedTemplateComponent(ctx: StateContext<TemplatesStateModel>, { payload }: SetSelectedTemplateComponent) {

    let editedRowIndex = null;
    if (payload) {
      const rowData = this._store.selectSnapshot(SharedGridState.getGridConfig)[this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID].rowData;
      editedRowIndex = rowData.findIndex(row => row.value === payload.value && row.action === payload.action);
    }
    ctx.patchState({
      selectedTemplateComponent: payload,
      editedRowIndex: editedRowIndex
    });
  }

  @Action(OnSubmitValues)
  onSubmitNewValues(ctx: StateContext<TemplatesStateModel>) {
    ctx.dispatch(new SetShowLoader(true));
    const updatedRowData = ctx.getState().templates;
    const templateFooterForm = ctx.getState().templateFooterForm;
    const footer = templateFooterForm.model.valueFooter;
    const type = ctx.getState().type;
    const key = ctx.getState().key;
    const stakeholders = templateFooterForm.model.stakeholders;
    const payload = { type, key, stakeholders: [], valueFooter: footer, edited: false, rows: [] };

    payload.type = type;
    payload.key = key;
    payload.stakeholders = stakeholders;
    payload.valueFooter = footer;
    payload.edited = templateFooterForm.dirty;
    for (const row of updatedRowData) {
      if (!row._id) {
        payload['rows'].push({
          value: row.value,
          action: row.action,
          new: true,
          edited: false
        });
      } else {
        payload['rows'].push({
          _id: row._id,
          value: row.value,
          action: row.action,
          new: false,
          edited: row.edited,
        });
      }
    }
    return this._templatesService.createOrUpdateTemplate(payload).pipe(
      tap((response: Template[]) => {
        if (response) {
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'success',
            messageTitle: 'Success',
            messageBody: 'Template saved successfully',
            position: 'toast-top-center'
          }));
        } else {
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'Error',
            messageBody: 'Template save failed',
            position: 'toast-top-center'
          }));
        }
        ctx.dispatch(new SetShowLoader(false));
        ctx.patchState({ templates: [] });
        ctx.patchState({ templates: response });
        this._store.dispatch(new OnSetSharedGridRowData({ update: true, payload: response, gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
        if (ctx.getState().isNew) {
          ctx.dispatch(new OnSetSharedGridRowData({ payload: [payload], gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
          ctx.dispatch(new SetIsNew(false));
        }
      }));
  }

  @Action(OnRemoveTemplate)
  onRemoveTemplate(ctx: StateContext<TemplatesStateModel>, { payload }: OnRemoveTemplate) {
    return this._templatesService.deleteTemplate(payload).pipe(
      catchError((error) => {
        console.error('Error deleting template:', error);
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: `Error deleting template: ${error.error.message.message}`,
          position: 'toast-top-center'
        }));
        ctx.dispatch(new SetShowLoader(false));
        throw error;
      }),
      tap(() => {
        this._store.dispatch(new OnRemoveSharedGridRowData({
          gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID,
          removeRow: payload
        }));
        ctx.dispatch(new SetShowLoader(false));
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'success',
          messageTitle: 'Success',
          messageBody: `Template deleted for ${payload.type}.`,
          position: 'toast-top-center'
        }));
      })
    );
  }
  @Action(GetFormData)
  async getPurchaseShippingTerms(ctx: StateContext<TemplatesStateModel>) {
    if (ctx.getState().isNew && !ctx.getState().isClone) {
      ctx.patchState({ templateSalesShippingInstructionsFormData: null });
      ctx.patchState({ templatePurchaseTermsFormData: null });
      ctx.patchState({ templateSingleFormFieldData: null });
      return;
    }
    const payload = { type: ctx.getState().type, stakeholders: ctx.getState().stakeholders };
    return this._templatesService.getFormValues(payload).pipe(
      catchError((error) => {
        console.error('Error deleting template:', error);
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: `Error deleting template: ${error.error.message.message}`,
          position: 'toast-top-center'
        }));
        ctx.dispatch(new SetShowLoader(false));
        throw error;
      }),
      tap((result: Template[]) => {
        if (!ctx.getState().isClone) {
          ctx.patchState({ templates: result });
        } else {
          const strippedResult = result.map(template => ({
            ...template,
            _id: null,
            stakeholders: [],
          }));
          ctx.patchState({ stakeholders: null });
          ctx.patchState({ templates: strippedResult });
        }
        const doc = result.reduce((acc: any, { key, value }) => {
          acc[key] = value;
          return acc;
        }, {});
        if (ctx.getState().type == 'purchaseShippingTerms') ctx.patchState({ templatePurchaseTermsFormData: doc });
        if (ctx.getState().type == 'salesShippingInstructions') ctx.patchState({ templateSalesShippingInstructionsFormData: doc });
        if (ctx.getState().type == 'salesConfirmationTerms') ctx.patchState({ templateSingleFormFieldData: { input: doc.salesConfirmationTerms, stakeholders: ctx.getState().stakeholders } });
        if (ctx.getState().type == 'purchaseConfirmationTerms') ctx.patchState({ templateSingleFormFieldData: { input: doc.purchaseConfirmationTerms, stakeholders: ctx.getState().stakeholders } });
        if (ctx.getState().type == 'offerTerms') ctx.patchState({ templateSingleFormFieldData: { input: doc.offerTerms, stakeholders: ctx.getState().stakeholders } });
      }));
  }



  @Action(SavePurchaseShippingTerms)
  async savePurchaseShippingTerms(ctx: StateContext<TemplatesStateModel>) {
    const state = ctx.getState();
    const formData = ctx.getState().templatePurchaseTermsForm.model;
    const stakeholderFormData = ctx.getState().stakeholderForm.model.stakeholders;
    if (state.isNew) {
      const payload = Object.entries(formData).map(([key, value]) => ({
        type: state.type,
        key: key,
        value: value,
        stakeholders: stakeholderFormData
      }));
      return this._templatesService.createTemplateForm(payload).pipe(
        catchError((error) => {
          console.error('Error deleting template:', error);
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'Error',
            messageBody: `Error creating: ${error.error.message.message}`,
            position: 'toast-top-center'
          }));
          ctx.dispatch(new SetShowLoader(false));
          throw error;
        }),
        tap((result) => {
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'success',
            messageTitle: 'Success',
            messageBody: `Template Succesfully Created`,
            position: 'toast-top-center'
          }));
          ctx.dispatch(new OnSetSharedGridRowData({ payload: [result[0]], gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
          ctx.dispatch(new SetIsNew(false));
        }));
    }
    let updatedTemplates;
    if (ctx.getState().stakeholderForm.model.dirty) {
      updatedTemplates = state.templates.map(template => ({
        ...template,
        edited: true,
        stakeholders: stakeholderFormData,
      }));
    } else updatedTemplates = state.templates;
    const finalTemplates = updatedTemplates.map(template => {
      const newValue = formData[template.key];
      return newValue !== undefined && newValue !== template.value
        ? { ...template, value: newValue, edited: true }
        : { ...template, edited: true, stakeholders: stakeholderFormData };
    });
    ctx.patchState({ templates: updatedTemplates });
    return this._templatesService.updateTemplateForm(finalTemplates).pipe(
      catchError((error) => {
        console.error('Error deleting template:', error);
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: `Error updating: ${error.error.message.message}`,
          position: 'toast-top-center'
        }));
        ctx.dispatch(new SetShowLoader(false));
        throw error;
      }),
      tap(() => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'success',
          messageTitle: 'Success',
          messageBody: `Template Succesfully Updated`,
          position: 'toast-top-center'
        }));
        this._store.dispatch(new OnSetSharedGridRowData({ update: true, payload: finalTemplates, gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
        ctx.dispatch(new SetIsNew(false));
      }));
  }

  @Action(SaveSalesShippingInstructions)
  async saveSalesShippingInstructions(ctx: StateContext<TemplatesStateModel>) {
    const state = ctx.getState();
    const formData = ctx.getState().templateSalesShippingInstructionsForm.model;
    const stakeholderFormData = ctx.getState().stakeholderForm.model.stakeholders;
    if (state.isNew) {
      const payload = Object.entries(formData).map(([key, value]) => ({
        type: state.type,
        key: key,
        value: value,
        stakeholders: stakeholderFormData
      }));
      return this._templatesService.createTemplateForm(payload).pipe(
        catchError((error) => {
          console.error('Error deleting template:', error);
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'Error',
            messageBody: `Error creating: ${error.error.message.message}`,
            position: 'toast-top-center'
          }));
          ctx.dispatch(new SetShowLoader(false));
          throw error;
        }),
        tap((result) => {
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'success',
            messageTitle: 'Success',
            messageBody: `Template Succesfully Created`,
            position: 'toast-top-center'
          }));
          ctx.dispatch(new OnSetSharedGridRowData({ payload: [result[0]], gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
          ctx.dispatch(new SetIsNew(false));
        }));
    }
    let updatedTemplates;
    if (ctx.getState().stakeholderForm.model.dirty) {
      updatedTemplates = state.templates.map(template => ({
        ...template,
        edited: true,
        stakeholders: stakeholderFormData,
      }));
    } else {
      updatedTemplates = state.templates;
    }
    const finalTemplates = updatedTemplates.map(template => {
      const newValue = formData[template.key];
      return newValue !== undefined && newValue !== template.value
        ? { ...template, value: newValue, edited: true }
        : { ...template, edited: true, stakeholders: stakeholderFormData };
    });
    ctx.patchState({ templates: finalTemplates });
    return this._templatesService.updateTemplateForm(finalTemplates).pipe(
      catchError((error) => {
        console.error('Error deleting template:', error);
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: `Error updating: ${error.error.message.message}`,
          position: 'toast-top-center'
        }));
        ctx.dispatch(new SetShowLoader(false));
        throw error;
      }),
      tap(() => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'success',
          messageTitle: 'Success',
          messageBody: `Template Succesfully Updated`,
          position: 'toast-top-center'
        }));
        this._store.dispatch(new OnSetSharedGridRowData({ update: true, payload: finalTemplates, gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
      }));
  }

  @Action(SaveSingleForm)
  async saveSingleForm(ctx: StateContext<TemplatesStateModel>) {
    const state = ctx.getState();
    const formData = ctx.getState().templateSingleForm.model;
    const newValue = formData.input;
    const newStakeholders = formData.stakeholders;
    if (state.isNew) {
      const payload = [{
        type: state.type,
        key: state.type,
        value: newValue,
        stakeholders: newStakeholders,
      }];
      return this._templatesService.createTemplateForm(payload).pipe(
        catchError((error) => {
          console.error('Error deleting template:', error);
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'error',
            messageTitle: 'Error',
            messageBody: `Error creating: ${error.error.message.message}`,
            position: 'toast-top-center'
          }));
          ctx.dispatch(new SetShowLoader(false));
          throw error;
        }),
        tap((result) => {
          ctx.dispatch(new ShowToastrAlert({
            showToast: true,
            toastrType: 'success',
            messageTitle: 'Success',
            messageBody: `Template Succesfully Created`,
            position: 'toast-top-center'
          }));
          ctx.dispatch(new OnSetSharedGridRowData({ payload: [result[0]], gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
          ctx.dispatch(new SetIsNew(false));

        }));
    }
    const updatedTemplates = state.templates.map(template =>
      template.type === ctx.getState().type ? { ...template, value: newValue, edited: true, stakeholders: newStakeholders ?? [] } : template
    );
    ctx.patchState({ templates: updatedTemplates });
    return this._templatesService.updateTemplateForm(updatedTemplates).pipe(
      catchError((error) => {
        console.error('Error deleting template:', error);
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: `Error updating: ${error.error.message.message}`,
          position: 'toast-top-center'
        }));
        ctx.dispatch(new SetShowLoader(false));
        throw error;
      }),
      tap(() => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'success',
          messageTitle: 'Success',
          messageBody: `Template Succesfully Updated`,
          position: 'toast-top-center'
        }));
      }));
  }

  @Action(SetNameMap)
  getNameMap(ctx: StateContext<TemplatesStateModel>) {
    return this._templatesService.getStakeholdersMap().pipe(
      tap((result: NameMap[]) => {
        ctx.patchState({ nameMap: result });
      })
    );
  }
  @Action(SetSelectedTemplateRowData)
  setSelectedTemplateRowData(ctx: StateContext<TemplatesStateModel>, { payload }: SetSelectedTemplateRowData) {
    ctx.patchState({ selectedTemplateRowData: payload });
  }

  @Action(IsSelectedGridDataEditable)
  isSelectedGridDataEditable(ctx: StateContext<TemplatesStateModel>, { payload }: IsSelectedGridDataEditable) {
    ctx.patchState({ isSelectedRowEditable: payload });
  }

  @Action(SetIsNew)
  setIsNew(ctx: StateContext<TemplatesStateModel>, { payload }: SetIsNew) {
    ctx.patchState({ isNew: payload });
  }

  @Action(SetIsGeneric)
  setHasEmptyStakeholderTemplates(ctx: StateContext<TemplatesStateModel>, { payload }: SetIsGeneric) {
    ctx.patchState({ isGeneric: payload });
  }

  @Action(SetIsClone)
  setIsClone(ctx: StateContext<TemplatesStateModel>, { payload }: SetIsClone) {
    ctx.patchState({ isClone: payload });
  }

  @Action(SetSelectedStakeholders)
  setSelectedStakeholders(ctx: StateContext<TemplatesStateModel>, { payload }: SetSelectedStakeholders) {
    return ctx.patchState({
      stakeholders: payload,
    });
  }

  @Selector()
  static getIsClone(state: TemplatesStateModel) {
    return state.isClone;
  }

  @Selector()
  static getPurchaseTermsFormData(state: TemplatesStateModel) {
    return state.templatePurchaseTermsFormData;
  }

  @Selector()
  static getSalesShippingInstructionFormData(state: TemplatesStateModel) {
    return state.templateSalesShippingInstructionsFormData;
  }

  @Selector()
  static getSingleFormData(state: TemplatesStateModel) {
    return state.templateSingleFormFieldData;
  }


  @Selector()
  static getActionDropdownOptions(state: TemplatesStateModel) {
    return state.actionFormDropdowns;
  }

  @Selector()
  static getTemplatesState(state: TemplatesStateModel) {
    return state.templates;
  }

  @Selector()
  static getTemplateType(state: TemplatesStateModel) {
    return state.type;
  }

  @Selector()
  static getTemplateKey(state: TemplatesStateModel) {
    return state.key;
  }

  @Selector()
  static getTemplateStakeholders(state: TemplatesStateModel) {
    return state.stakeholders;
  }

  @Selector()
  static getTemplateFooterValue(state: TemplatesStateModel) {
    return state.valueFooter;
  }

  @Selector()
  static getIsSelectedRowEditable(state: TemplatesStateModel) {
    return state.isSelectedRowEditable;
  }

  @Selector()
  static getSelectedTemplateRowData(state: TemplatesStateModel) {
    return state.selectedTemplateRowData;
  }

  @Selector()
  static getIsNew(state: TemplatesStateModel) {
    return state.isNew;
  }

  @Selector()
  static getNameMap(state: TemplatesStateModel) {
    return state.nameMap;
  }

  @Selector()
  static getIsGeneric(state: TemplatesStateModel) {
    return state.isGeneric;
  }

  @Selector()
  static getStakeholderDropdowns(state: TemplatesStateModel) {
    return state.stakeholderDropdowns;
  }

}